import * as React from "react";

import { assertNever } from "@impero-com/common/utils/obj";
import { Checkbox } from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";

import { EditFormCreate, ValidationRule } from "utils/form";

import { BaseFormDisplay } from "./formDisplay";

import { BaseFormField, EditInput, EditProps } from ".";

interface MultiCheckboxInputReadOnlyProps {
    label: string;
    values?: Array<boolean | undefined | null>;
}

interface MultiCheckboxInputWriteProps extends MultiCheckboxInputReadOnlyProps {
    onCheckAllChange?: (e: CheckboxChangeEvent) => void;
    disabled?: boolean;
}

interface CreateMultiCheckboxInputProps {
    validators?: ValidationRule<Array<boolean>>[];
}

class RawReadOnlyMultiCheckboxInput extends React.PureComponent<MultiCheckboxInputReadOnlyProps> {
    render() {
        const { label, values } = this.props;
        return (
            <Checkbox
                indeterminate={isIndeterminate(values || [])}
                checked={allValuesAreTrue(values || [])}
                disabled
            >
                {label}
            </Checkbox>
        );
    }
}

class ReadOnlyMultiCheckboxInput extends React.PureComponent<MultiCheckboxInputReadOnlyProps> {
    render() {
        return (
            <BaseFormField
                getComponent={this.getComponent}
                hasFeedback={false}
            />
        );
    }

    private getComponent = () => {
        const { label, values } = this.props;
        return <RawReadOnlyMultiCheckboxInput label={label} values={values} />;
    };
}

class RawCreateMultiCheckboxInput extends React.PureComponent<MultiCheckboxInputWriteProps> {
    render() {
        const { label, values, disabled } = this.props;
        return (
            <Checkbox
                onChange={this.onMultiCheckboxChange}
                indeterminate={isIndeterminate(values || [])}
                checked={allValuesAreTrue(values || [])}
                disabled={disabled}
            >
                {label}
            </Checkbox>
        );
    }

    private onMultiCheckboxChange = (e: CheckboxChangeEvent) => {
        const { onCheckAllChange } = this.props;

        if (onCheckAllChange) {
            onCheckAllChange(e);
        }
    };
}

class CreateMultiCheckboxInput extends React.PureComponent<MultiCheckboxInputWriteProps> {
    render() {
        return <BaseFormDisplay>{this.getComponent()}</BaseFormDisplay>;
    }

    getComponent = () => {
        const { label, values, disabled, onCheckAllChange } = this.props;
        return (
            <RawCreateMultiCheckboxInput
                values={values}
                label={label}
                disabled={disabled}
                onCheckAllChange={onCheckAllChange}
            />
        );
    };
}

interface EditMultiCheckboxInputProps extends EditProps<
    Array<boolean | undefined | null> | undefined
> {
    label: string;
    disabled?: boolean;
    onCheckAllChange?: ((e: CheckboxChangeEvent) => void) | undefined;
    values: (boolean | null | undefined)[] | undefined;
}

const EditMultiCheckboxInputForm = EditFormCreate<
    Array<boolean | undefined | null>,
    EditMultiCheckboxInputProps
>()(CreateMultiCheckboxInput);
const EditMultiCheckboxInput = EditInput<
    Array<boolean | undefined | null>,
    EditMultiCheckboxInputProps
>(EditMultiCheckboxInputForm);

const RawEditMultiCheckboxInputForm = EditFormCreate<
    Array<boolean | undefined | null>,
    EditMultiCheckboxInputProps
>()(RawCreateMultiCheckboxInput);
const RawEditMultiCheckboxInput = EditInput<
    Array<boolean | undefined | null>,
    EditMultiCheckboxInputProps
>(RawEditMultiCheckboxInputForm);

type MultiCheckboxInputProps =
    | ({ type: "READ_ONLY" } & MultiCheckboxInputReadOnlyProps)
    | ({ type: "EDIT" } & EditMultiCheckboxInputProps &
          MultiCheckboxInputWriteProps)
    | ({ type: "CREATE" } & CreateMultiCheckboxInputProps &
          MultiCheckboxInputWriteProps);

class MultiCheckboxInput extends React.PureComponent<MultiCheckboxInputProps> {
    render = () => {
        const { props } = this;
        switch (props.type) {
            case "READ_ONLY": {
                const { label, values } = props;
                return (
                    <ReadOnlyMultiCheckboxInput label={label} values={values} />
                );
            }
            case "EDIT": {
                const {
                    id,
                    value,
                    values,
                    label,
                    onChange,
                    onCheckAllChange,
                    validators,
                    disabled,
                } = props;
                return (
                    <EditMultiCheckboxInput
                        onCheckAllChange={onCheckAllChange}
                        disabled={disabled}
                        label={label}
                        id={id}
                        value={value}
                        onChange={onChange}
                        values={values}
                        validators={validators}
                    />
                );
            }
            case "CREATE": {
                const { values, onCheckAllChange, label, disabled } = props;
                return (
                    <CreateMultiCheckboxInput
                        values={values}
                        onCheckAllChange={onCheckAllChange}
                        label={label}
                        disabled={disabled}
                    />
                );
            }
            default:
                return assertNever(props);
        }
    };
}

class RawMultiCheckboxInput extends React.PureComponent<MultiCheckboxInputProps> {
    render = () => {
        switch (this.props.type) {
            case "READ_ONLY": {
                const { label, values } = this.props;
                return (
                    <RawReadOnlyMultiCheckboxInput
                        label={label}
                        values={values}
                    />
                );
            }
            case "EDIT": {
                const {
                    id,
                    value,
                    onChange,
                    validators,
                    label,
                    disabled,
                    onCheckAllChange,
                    values,
                } = this.props;
                return (
                    <RawEditMultiCheckboxInput
                        id={id}
                        onCheckAllChange={onCheckAllChange}
                        label={label}
                        disabled={disabled}
                        value={value}
                        onChange={onChange}
                        values={values}
                        validators={validators}
                    />
                );
            }
            case "CREATE": {
                const { values, onCheckAllChange, label, disabled } =
                    this.props;
                return (
                    <RawCreateMultiCheckboxInput
                        values={values}
                        onCheckAllChange={onCheckAllChange}
                        label={label}
                        disabled={disabled}
                    />
                );
            }
            default:
                return assertNever(this.props);
        }
    };
}

function isIndeterminate(values: Array<boolean | undefined | null>): boolean {
    const allValuesAsPrimitiveBooleans = values.map((value) => !!value);
    return (
        allValuesAsPrimitiveBooleans.includes(true) &&
        allValuesAsPrimitiveBooleans.includes(false)
    );
}

function allValuesAreTrue(values: Array<boolean | undefined | null>): boolean {
    return values.length !== 0 && values.every((value) => !!value);
}

export {
    isIndeterminate,
    allValuesAreTrue,
    MultiCheckboxInput,
    RawMultiCheckboxInput,
};
